import { FC, forwardRef, useCallback, useState } from 'react';

import { AsyncDataLoader } from '@hh.ru/magritte-ui-date-picker/AsyncDataLoader';
import { AsyncDataStore } from '@hh.ru/magritte-ui-date-picker/AsyncDataStore';
import { DayPicker } from '@hh.ru/magritte-ui-date-picker/DayPicker';
import { Header } from '@hh.ru/magritte-ui-date-picker/Header';
import { MonthPicker } from '@hh.ru/magritte-ui-date-picker/MonthPicker';
import { Weekdays } from '@hh.ru/magritte-ui-date-picker/Weekdays';
import { WithArrowNavigationHOC } from '@hh.ru/magritte-ui-date-picker/WithArrowNavigationHOC';
import { YearPicker } from '@hh.ru/magritte-ui-date-picker/YearPicker';
import { DateType, DatePickerProps } from '@hh.ru/magritte-ui-date-picker/types';
import { useDatePickerControls } from '@hh.ru/magritte-ui-date-picker/useDatePickerControls';
import { useDisplayDate } from '@hh.ru/magritte-ui-date-picker/utils';
import { Divider } from '@hh.ru/magritte-ui-divider';

import styles from './date-picker.less';

const DatePickerRenderFunc = forwardRef<HTMLDivElement, DatePickerProps>(
    (
        {
            // настройки функциональности
            disabledDates,
            disabledDatesFunc,
            displayDate: _displayDate,
            enabledCalendars,
            minDate,
            maxDate,
            selectedDate,
            // колбеки
            onDisplayDateChange,
            onDateSelect,
            // настройки внешнего вида
            activeHeader,
            alignHeader,
            headerMonthFormat,
            locale,
            showAdjacentDays = true,
            showControls,
            showHeader = true,
            showHeaderDate,
            showDivider = true,
            showWeekdays = true,
        },
        ref
    ) => {
        const displayDate = useDisplayDate(_displayDate, selectedDate);
        const { calendar, switchCalendar, zoomIntoMonth, zoomIntoYear, goToNextPeriod, goToPrevPeriod } =
            useDatePickerControls(enabledCalendars, displayDate, onDisplayDateChange);

        return (
            <div className={styles.layout} ref={ref}>
                <AsyncDataStore disabledDatesFunc={disabledDatesFunc}>
                    <AsyncDataLoader>
                        {showHeader && (
                            <Header
                                calendar={calendar}
                                displayDate={displayDate}
                                activeHeader={activeHeader}
                                alignHeader={alignHeader}
                                goToNextPeriod={goToNextPeriod}
                                goToPrevPeriod={goToPrevPeriod}
                                headerMonthFormat={headerMonthFormat}
                                locale={locale}
                                showControls={showControls}
                                showHeaderDate={showHeaderDate}
                                switchCalendar={switchCalendar}
                            />
                        )}
                        {showWeekdays && calendar === 'day' && <Weekdays locale={locale} />}
                        {showDivider && (showHeader || showWeekdays) && <Divider />}
                        {calendar === 'day' && (
                            <DayPicker
                                disabledDates={disabledDates}
                                displayDate={displayDate}
                                selectedDate={selectedDate}
                                onDateSelect={onDateSelect}
                                showAdjacentDays={showAdjacentDays}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        )}
                        {calendar === 'month' && (
                            <MonthPicker
                                locale={locale}
                                displayDate={displayDate}
                                selectedDate={selectedDate}
                                onDateSelect={enabledCalendars.startsWith('dd') ? zoomIntoMonth : onDateSelect}
                            />
                        )}
                        {calendar === 'year' && (
                            <YearPicker
                                displayDate={displayDate}
                                selectedDate={selectedDate}
                                onDateSelect={zoomIntoYear}
                            />
                        )}
                    </AsyncDataLoader>
                </AsyncDataStore>
            </div>
        );
    }
);

DatePickerRenderFunc.displayName = 'DatePickerRenderFunc';

export const DatePicker = WithArrowNavigationHOC(DatePickerRenderFunc);

export const UncontrolledDatePicker: FC<DatePickerProps> = ({
    displayDate: initialDisplayDate,
    selectedDate: initialSelectedDate,
    onDisplayDateChange,
    onDateSelect,
    ...props
}) => {
    const [displayDate, setDisplayDate] = useState<DateType>(useDisplayDate(initialDisplayDate, initialSelectedDate));
    const [selectedDate, setSelectedDate] = useState<DateType | null>(initialSelectedDate ?? null);

    const handleDisplayDateChange = useCallback(
        (value: DateType) => {
            onDisplayDateChange?.(value);
            setDisplayDate(value);
        },
        [onDisplayDateChange, setDisplayDate]
    );

    const handleDateSelect = useCallback(
        (value: DateType) => {
            onDateSelect?.(value);
            setSelectedDate(value);
        },
        [onDateSelect, setSelectedDate]
    );

    return (
        <DatePicker
            {...props}
            displayDate={displayDate}
            selectedDate={selectedDate}
            onDisplayDateChange={handleDisplayDateChange}
            onDateSelect={handleDateSelect}
        />
    );
};
